import { defineStore } from "pinia";
import { ChartPeriodEnum } from "~/enums";
import {
  GeoLocalizationsRes2GeoLocalizations,
  CountriesToSeries,
  ReferrerToSeries
} from "~/mappers";
import type { IHistogramsGroup } from "~/types/IImpactV3Histograms";
import type { IGeolocalization } from "~/types/components";

interface IImpactV3StoreState {
  totalViews: number;
  defaultViewScaleOption: string;
  histogramGroups: Map<string, IHistogramsGroup>;
  geolocations?: IGeolocalization[];
  topCountries?: Highcharts.PointOptionsObject[];
  topReferrers?: Highcharts.PointOptionsObject[];
}

export const useImpactStore = defineStore("impact", {
  state: (): IImpactV3StoreState => ({
    totalViews: 0,
    defaultViewScaleOption: ChartPeriodEnum.beginning,
    histogramGroups: new Map<string, IHistogramsGroup>(),
    geolocations: undefined,
    topCountries: undefined,
    topReferrers: undefined
  }),
  getters: {
    getHistogramsInfo: (state) => (): Map<string, IHistogramsGroup> =>
      state.histogramGroups,
    hasHistogramsInfo: (state) => (): boolean =>
      state.histogramGroups.size !== 0,
    getTotalViews: (state) => (): number => state.totalViews
  },
  actions: {
    async fetchImpactV3Histogram(researchTopicId: number) {
      const { getHistogram } = useImpactV3Service();
      const data = await getHistogram(researchTopicId);

      if (data) {
        this.totalViews = data.totalViews;
        this.defaultViewScaleOption = data.defaultViewScaleOption;
        data.histogramGroups.forEach((group: IHistogramsGroup) => {
          if (!this.histogramGroups.has(group.timeGranularity)) {
            this.histogramGroups.set(group.timeGranularity, group);
          }
        });
      } else {
        this.histogramGroups.set("NoData", {} as IHistogramsGroup);
      }
    },
    async fetchGeolocations(researchTopicId: number) {
      const { getGeolocations } = useImpactV3Service();
      const { geolocalizations } = await getGeolocations(researchTopicId);

      this.geolocations =
        GeoLocalizationsRes2GeoLocalizations(geolocalizations);
    },

    async fetchTopCountries(researchTopicId: number) {
      const { getTopCountries } = useImpactV3Service();
      const { topCountries } = await getTopCountries(researchTopicId);

      this.topCountries = CountriesToSeries(topCountries);
    },

    async fetchTopReferrers(researchTopicId: number) {
      const { getTopReferrers } = useImpactV3Service();
      const { topReferrers } = await getTopReferrers(researchTopicId);

      this.topReferrers = ReferrerToSeries(topReferrers);
    }
  }
});
